import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  submitRegularizationApi,
  fetchAllRegularizationApplicationsApi,
  withdrawRegularizationApi,
  approveOrRejectRegularizationApi,
  createOrUpdateManualOverrideAPI,
  getUserManualOverridesAPI,
} from "../../api/regularization.api";
import { RegularizationPayload } from "../../models/regularize.model";
import { updateAttendanceRecord } from "./attendanceSlice";

interface Regularization {
  _id: string;
  date: string;
  reason: string;
  status: "pending" | "approved" | "rejected" | "withdrawn";
}

interface RegularizationApplicationsResponse {
  pendingRegularizationApplications: Regularization[];
  regularizationHistory: Regularization[];
}

interface RegularizeState {
  pendingRegularizationApplications: Regularization[];
  regularizationHistory: Regularization[];
  loading: boolean;
  error: string | null;
  success: boolean;
  overrides: any[];
}

const initialState: RegularizeState = {
  pendingRegularizationApplications: [],
  regularizationHistory: [],
  loading: false,
  error: null,
  success: false,
  overrides: [],
};

export const submitRegularization = createAsyncThunk<
  any,
  RegularizationPayload,
  { rejectValue: string }
>("regularize/submit", async (payload, { rejectWithValue }) => {
  try {
    const data = await submitRegularizationApi(payload);
    return data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || err.message);
  }
});

export const fetchAllRegularizeRequests = createAsyncThunk<
  RegularizationApplicationsResponse,
  void,
  { rejectValue: string }
>("regularize/fetchAllRequests", async (_, { rejectWithValue }) => {
  try {
    const data = await fetchAllRegularizationApplicationsApi();
    return data as RegularizationApplicationsResponse;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || err.message);
  }
});

export const withdrawRegularization = createAsyncThunk<
  string,
  string,
  { rejectValue: string }
>("regularize/withdraw", async (applicationId, { rejectWithValue }) => {
  try {
    await withdrawRegularizationApi(applicationId);
    return applicationId;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || err.message);
  }
});

// Create or Update Override
export const createOrUpdateManualOverride = createAsyncThunk<
  any,
  {
    userId: string;
    date: string;
    session1Status: "Present" | "Absent";
    session2Status: "Present" | "Absent";
    remarks?: string
  },
  { rejectValue: string }
>(
  "regularize/createOrUpdateManualOverride",
  async (data, {dispatch, rejectWithValue }) => {
    try {
      const resp = await createOrUpdateManualOverrideAPI(data);
      dispatch(
        updateAttendanceRecord({
          date: data.date,
          session1Status: data.session1Status,
          session2Status: data.session2Status,
        })
      );
      return resp;
    } catch (err: any) {
      return rejectWithValue(err.message || "Failed to create/update override");
    }
  }
);

// Fetch Overrides for User
export const fetchUserManualOverrides = createAsyncThunk<
  any,
  string,
  { rejectValue: string }
>(
  "regularize/fetchUserManualOverrides",
  async (userId, { rejectWithValue }) => {
    try {
      const resp = await getUserManualOverridesAPI(userId);
      return resp;
    } catch (err: any) {
      return rejectWithValue(err.message || "Failed to fetch overrides");
    }
  }
);


const regularizeSlice = createSlice({
  name: "regularize",
  initialState,
  reducers: {
    reset: (state) => {
      state.loading = false;
      state.error = null;
      state.success = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Submit
      .addCase(submitRegularization.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(submitRegularization.fulfilled, (state) => {
        state.loading = false;
        state.success = true;
      })
      .addCase(submitRegularization.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload ?? "Failed to submit regularization request";
      })

      // Fetch
      .addCase(fetchAllRegularizeRequests.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchAllRegularizeRequests.fulfilled,
        (state, action: PayloadAction<RegularizationApplicationsResponse>) => {
          state.pendingRegularizationApplications =
            action.payload.pendingRegularizationApplications || [];
          state.regularizationHistory =
            action.payload.regularizationHistory || [];
          state.loading = false;
        }
      )
      .addCase(fetchAllRegularizeRequests.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload ?? "Failed to fetch regularization requests";
      })
      .addCase(withdrawRegularization.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(withdrawRegularization.fulfilled, (state, action) => {
        state.loading = false;
        const withdrawnApp = state.pendingRegularizationApplications.find(
          (req) => req._id === action.payload
        );
        if (withdrawnApp) {
          withdrawnApp.status = "withdrawn";
          state.regularizationHistory = [
            withdrawnApp,
            ...state.regularizationHistory,
          ];
        }
        state.pendingRegularizationApplications =
          state.pendingRegularizationApplications.filter(
            (req) => req._id !== action.payload
          );
      })

      .addCase(withdrawRegularization.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload ?? "Failed to withdraw regularization request";
      })
      .addCase(createOrUpdateManualOverride.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createOrUpdateManualOverride.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.overrides.findIndex(
          (o: { userId: any; date: any }) =>
            o.userId === action.payload.userId && o.date === action.payload.date
        );
        if (index !== -1) {
          state.overrides[index] = action.payload; // update existing
        } else {
          state.overrides.unshift(action.payload); // add new
        }
      })
      .addCase(createOrUpdateManualOverride.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to create/update override";
      })

      // Fetch
      .addCase(fetchUserManualOverrides.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserManualOverrides.fulfilled, (state, action) => {
        state.loading = false;
        state.overrides = action.payload || [];
      })
      .addCase(fetchUserManualOverrides.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch overrides";
      });
  },
});

export const { reset } = regularizeSlice.actions;
export default regularizeSlice.reducer;
